import { useEffect } from 'react';

import {
  sendbirdSelectors,
  useSendbird,
  useSendbirdStateContext,
} from '@sendbird/uikit-react';
import UserEventHandler from '@sendbird/uikit-react/handlers/UserEventHandler';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';

import { selectCurrentMembership } from '../auth/selectors';
import { setUnreadMessageCount } from './chat.slice';

const useSdkEventHandler = () => {
  const currentMembership = useAppSelector(selectCurrentMembership);
  const dispatch = useAppDispatch();
  const globalStore = useSendbirdStateContext();
  const sdkInstance = sendbirdSelectors.getSdk(globalStore);
  // Need to check user because sendbird will blow up if it's undefined
  // in GroupChannelListHeader.js when looking up user.profileUrl
  const sendbird = useSendbird();

  useEffect(() => {
    if (
      // I don't trust sendbird's types as far as I can throw them so gonna ignore this
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      !sdkInstance.addUserEventHandler ||
      // This can also be undefined after switching orgs
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      !sendbird.state.stores.userStore.user ||
      !currentMembership?.id
    ) {
      return;
    }

    const userEventHandler = new UserEventHandler({
      onTotalUnreadMessageCountUpdated: (totalCount) => {
        dispatch(setUnreadMessageCount(totalCount));
      },
    });

    sdkInstance.addUserEventHandler(currentMembership.id, userEventHandler);

    return () => {
      sdkInstance.removeUserEventHandler(currentMembership.id || '');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkInstance, currentMembership?.id, dispatch]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // the types are wrong, sdkInstance can be undefined
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      sdkInstance?.disconnect();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sdkInstance]);
};

export default useSdkEventHandler;
